import type {
  Artifact as ArtifactContract,
  Greeting as GreetingContract,
  ActionResult as ResultContract,
  Setup as SetupContract,
  AppState as StateContract,
} from '@agent-bridge/contracts'
import { App as McpApp } from '@modelcontextprotocol/ext-apps'
export type Tone = GreetingContract['tone']
export type Greeting = Required<GreetingContract>
export type AppState = Omit<
  Required<StateContract>,
  'setup' | 'greeting' | 'history'
> & {
  setup: Required<SetupContract>
  greeting: Greeting | null
  history: Greeting[]
}
export type Artifact = Omit<ArtifactContract, 'data'> & { data: AppState }
export type ActionResult = Omit<ResultContract, 'data' | 'artifact'> & {
  data: AppState
  artifact: Artifact
}
export type ViewContext = {
  appInstanceId: string
  greetingId: string | null
  selectedRevision: number | null
  currentRevision: number
  excerpt: string
  [key: string]: unknown
}
export type Capabilities = {
  context: boolean
  messages: boolean
  tools: boolean
  navigation: boolean
  nativeWebMCP: boolean
}
export interface AppAdapter {
  capabilities: Capabilities
  state(): Promise<AppState>
  action(
    name: string,
    input: Record<string, unknown>,
    expectedRevision?: number,
  ): Promise<ActionResult>
  context(value: ViewContext): Promise<void>
  discuss(
    text: string,
    context: ViewContext,
    requestId: string,
    displayText?: string,
    interactionId?: string,
  ): Promise<string>
  inspect?(revision: number, latest?: boolean): Promise<void>
  subscribe?(callback: () => void): () => void
  dispose?(): void
}
export async function hostFetch(
  path: string,
  body?: unknown,
  method?: string,
): Promise<any> {
  const response = await fetch(path, {
    method: method ?? (body === undefined ? 'GET' : 'POST'),
    headers:
      body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const value = await response.json()
  if (!response.ok)
    throw new Error(
      typeof value.detail === 'string'
        ? value.detail
        : JSON.stringify(value.detail ?? value),
    )
  return value
}

export function actionPath(bindingId: string, name: string): string {
  if (!/^[a-zA-Z][a-zA-Z0-9_.-]*$/.test(name))
    throw new Error('Invalid application action name')
  return `/v1/apps/bindings/${encodeURIComponent(bindingId)}/actions/${encodeURIComponent(name)}`
}

type RegisteredTool = { name: string }
type ModelContext = {
  registerTool(
    tool: BrowserTool,
    options?: { signal: AbortSignal },
  ): Promise<void> | void
  unregisterTool?(name: string): void
  getTools?(): Promise<RegisteredTool[]>
  executeTool?(
    tool: RegisteredTool,
    input: Record<string, unknown>,
    options?: { signal: AbortSignal },
  ): Promise<string>
}
export type BrowserTool = {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  execute(
    input: Record<string, unknown>,
    options?: { signal?: AbortSignal },
  ): Promise<unknown>
}
export function nativeModelContext(): ModelContext | undefined {
  return (document as unknown as { modelContext?: ModelContext }).modelContext
}
export async function registerWebMCP(
  tools: BrowserTool[],
): Promise<() => void> {
  const api = nativeModelContext()
  if (!api) return () => {}
  const controller = new AbortController()
  for (const tool of tools)
    await api.registerTool(tool, { signal: controller.signal })
  return () => {
    controller.abort()
    for (const tool of tools) api.unregisterTool?.(tool.name)
  }
}
export async function executePageTool(
  tool: BrowserTool,
  input: Record<string, unknown>,
  signal: AbortSignal,
): Promise<unknown> {
  const api = nativeModelContext()
  if (api?.executeTool && api.getTools) {
    const registered = (await api.getTools()).find((t) => t.name === tool.name)
    if (!registered)
      throw new Error('The registered browser tool is unavailable')
    let result: string
    try {
      result = await api.executeTool(registered, input, { signal })
    } catch (error) {
      if (
        !(error instanceof DOMException) ||
        !error.message.includes('parse input arguments')
      )
        throw error
      result = await api.executeTool(
        registered,
        JSON.stringify(input) as unknown as Record<string, unknown>,
        { signal },
      )
    }
    return { route: 'native-webmcp', result: JSON.parse(result) }
  }
  return {
    route: 'bridge-shared-handler',
    result: await tool.execute(input, { signal }),
  }
}

export class ReferenceAdapter implements AppAdapter {
  capabilities: Capabilities = {
    context: true,
    messages: false,
    tools: true,
    navigation: true,
    nativeWebMCP: !!nativeModelContext(),
  }
  viewId = crypto.randomUUID()
  lease = ''
  currentContext: ViewContext | null = null
  private stopped = false
  private generation = 0
  private removeTools = () => {}
  private aborts = new Map<string, AbortController>()
  private onPageHide = () => this.dispose()
  onSelect: (revision: number) => void = () => {}
  onInspect: (revision: number, latest?: boolean) => void = () => {}
  constructor(
    public bindingId: string,
    public sessionId: string | null,
  ) {
    this.capabilities.messages = !!sessionId
  }
  async state(): Promise<AppState> {
    return hostFetch(`/v1/apps/bindings/${this.bindingId}/state`)
  }
  async action(
    name: string,
    input: Record<string, unknown>,
    expectedRevision?: number,
  ): Promise<ActionResult> {
    const id = crypto.randomUUID()
    return hostFetch(actionPath(this.bindingId, name), {
      input,
      expectedRevision,
      invocationId: id,
      idempotencyKey: id,
    })
  }
  async context(value: ViewContext): Promise<void> {
    this.currentContext = value
    if (!this.lease) return
    await hostFetch(
      `/v1/apps/bindings/${this.bindingId}/views/${this.viewId}/context`,
      {
        lease: this.lease,
        context: { ...value, viewId: this.viewId, lease: this.lease },
      },
      'PUT',
    )
  }
  async discuss(
    text: string,
    context: ViewContext,
    requestId: string,
    displayText?: string,
    interactionId?: string,
  ): Promise<string> {
    if (!this.capabilities.messages)
      throw new Error('Connect a chat agent to send this request.')
    await this.context(context)
    await hostFetch(`/v1/apps/bindings/${this.bindingId}/discuss`, {
      text,
      context,
      requestId,
      display_text: displayText,
      interaction_id: interactionId,
    })
    return 'Discussion request accepted.'
  }
  async inspect(revision: number, latest = false): Promise<void> {
    this.onInspect(revision, latest)
  }
  subscribe(callback: () => void): () => void {
    const timer = setInterval(callback, 1600)
    return () => clearInterval(timer)
  }
  async start(nativeTools = true): Promise<void> {
    const generation = ++this.generation
    this.stopped = false
    window.addEventListener('pagehide', this.onPageHide)
    this.viewId = crypto.randomUUID()
    const base = `/v1/apps/bindings/${this.bindingId}/views/${this.viewId}`
    const local: BrowserTool[] = [
      {
        name: 'hello.view_context',
        description: 'Read this live view selection',
        inputSchema: { type: 'object', properties: {} },
        execute: async () =>
          this.currentContext ?? { selectionAvailable: false },
      },
      {
        name: 'hello.select_revision',
        description: 'Select a saved greeting revision in this live view',
        inputSchema: {
          type: 'object',
          properties: { revision: { type: 'integer' } },
          required: ['revision'],
        },
        execute: async (input) => {
          const revision = Number(input.revision)
          const state = await this.state()
          if (!state.history.some((g) => g.revision === revision))
            throw new Error('Unknown revision')
          this.onSelect(revision)
          return { selectedRevision: revision }
        },
      },
    ]
    const lease = await hostFetch(base, { tools: local.map((t) => t.name) })
    if (generation !== this.generation) {
      void fetch(`${base}?lease=${encodeURIComponent(lease.lease)}`, {
        method: 'DELETE',
      })
      return
    }
    this.lease = lease.lease
    const catalog = await hostFetch(
      `/v1/apps/actions?bindingId=${encodeURIComponent(this.bindingId)}`,
    )
    const list = Array.isArray(catalog)
      ? catalog
      : (catalog.actions ?? catalog.tools ?? [])
    const domain: BrowserTool[] = list.map((t: any) => ({
      name: t.name ?? t.id ?? t.action,
      description: t.description,
      inputSchema: t.requestSchema ?? t.inputSchema,
      execute: async (args: Record<string, unknown>) => {
        if ((t.name ?? t.id ?? t.action) === 'hello.get_context')
          return this.currentContext ?? { selectionAvailable: false }
        const id = String(args.invocationId ?? crypto.randomUUID())
        return hostFetch(
          actionPath(this.bindingId, t.name ?? t.id ?? t.action),
          {
            input: args.input ?? {},
            expectedRevision: args.expectedRevision,
            invocationId: id,
            idempotencyKey: args.idempotencyKey ?? id,
          },
        )
      },
    }))
    if (generation !== this.generation) return
    if (nativeTools)
      this.removeTools = await registerWebMCP([...local, ...domain])
    if (this.currentContext) await this.context(this.currentContext)
    void (async () => {
      while (!this.stopped && generation === this.generation) {
        try {
          const call = await hostFetch(
            `${base}/poll?lease=${encodeURIComponent(this.lease)}`,
          )
          if (this.stopped || generation !== this.generation) break
          if (call.cancel) {
            this.aborts.get(call.cancel)?.abort()
            continue
          }
          if (call.heartbeat) continue
          const controller = new AbortController()
          this.aborts.set(call.invocationId, controller)
          void (async () => {
            let result: {
              content: { type: string; text: string }[]
              isError?: boolean
            }
            try {
              const tool = local.find((t) => t.name === call.tool)
              if (!tool) throw new Error('Tool is not approved')
              result = {
                content: [
                  {
                    type: 'text',
                    text: JSON.stringify(
                      nativeTools
                        ? await executePageTool(
                            tool,
                            call.input,
                            controller.signal,
                          )
                        : await tool.execute(call.input, {
                            signal: controller.signal,
                          }),
                    ),
                  },
                ],
              }
            } catch (error) {
              result = {
                isError: true,
                content: [{ type: 'text', text: String(error) }],
              }
            } finally {
              this.aborts.delete(call.invocationId)
            }
            if (!this.stopped)
              await hostFetch(`${base}/result`, {
                lease: this.lease,
                invocationId: call.invocationId,
                result,
              }).catch(() => {})
          })()
        } catch {
          if (!this.stopped) {
            this.capabilities.context = false
            break
          }
        }
      }
    })()
  }
  dispose(): void {
    window.removeEventListener('pagehide', this.onPageHide)
    this.generation++
    this.stopped = true
    this.removeTools()
    for (const c of this.aborts.values()) c.abort()
    if (this.lease)
      void fetch(
        `/v1/apps/bindings/${this.bindingId}/views/${this.viewId}?lease=${encodeURIComponent(this.lease)}`,
        { method: 'DELETE', keepalive: true },
      )
  }
}

export class McpAppsAdapter implements AppAdapter {
  capabilities: Capabilities = {
    context: false,
    messages: false,
    tools: false,
    navigation: false,
    nativeWebMCP: false,
  }
  onInspectView: (latest: boolean) => void = () => {}
  app = new McpApp(
    { name: 'Greeting Studio', version: '0.1.0' },
    { availableDisplayModes: ['inline', 'fullscreen'] },
  )
  constructor(
    public snapshot: AppState,
    public onSnapshot: (value: AppState) => void,
  ) {}
  async connect(): Promise<void> {
    this.app.ontoolresult = (result) => {
      const value = result.structuredContent as unknown as ActionResult
      if (value?.data) {
        this.snapshot = value.data
        this.onSnapshot(value.data)
      }
    }
    await this.app.connect()
    const c = this.app.getHostCapabilities()
    this.capabilities = {
      context: !!c?.updateModelContext,
      messages: !!c?.message,
      tools: !!c?.serverTools,
      navigation:
        !!c?.experimental?.['dev.agentbridge/inspection'] ||
        !!this.app
          .getHostContext()
          ?.availableDisplayModes?.includes('fullscreen'),
      nativeWebMCP: false,
    }
  }
  async state(): Promise<AppState> {
    const result = await this.action('hello.get_state', {})
    return result.data
  }
  async action(
    name: string,
    input: Record<string, unknown>,
    expectedRevision?: number,
  ): Promise<ActionResult> {
    if (!this.capabilities.tools)
      throw new Error('This host does not support tool callbacks.')
    const id = crypto.randomUUID()
    const result = await this.app.callServerTool({
      name,
      arguments: {
        input,
        invocationId: id,
        idempotencyKey: id,
        expectedRevision,
      },
    })
    if (result.isError)
      throw new Error(
        result.content
          .filter((c) => c.type === 'text')
          .map((c) => c.text)
          .join('\n'),
      )
    return result.structuredContent as unknown as ActionResult
  }
  async context(value: ViewContext): Promise<void> {
    if (this.capabilities.context)
      await this.app.updateModelContext({ structuredContent: value })
  }
  async discuss(
    text: string,
    context: ViewContext,
    _requestId: string,
    displayText?: string,
    interactionId?: string,
  ): Promise<string> {
    if (!this.capabilities.messages)
      throw new Error(
        'This host does not accept conversation requests. Copy the request into chat.',
      )
    await this.context(context)
    await this.app.sendMessage({
      role: 'user',
      content: [
        {
          type: 'text',
          text,
          ...(displayText !== undefined || interactionId !== undefined
            ? {
                _meta: {
                  'dev.agentbridge/interaction': {
                    displayText,
                    interactionId: interactionId ?? 'discuss',
                  },
                },
              }
            : {}),
        },
      ],
    })
    return 'Accepted by host'
  }
  async inspect(revision: number, latest = false): Promise<void> {
    if (
      this.app.getHostCapabilities()?.experimental?.[
        'dev.agentbridge/inspection'
      ]
    ) {
      await this.app.openLink({
        url: `https://bridge.invalid/inspect?revision=${revision}&latest=${latest}`,
      })
    } else {
      if (
        !this.app
          .getHostContext()
          ?.availableDisplayModes?.includes('fullscreen')
      )
        throw new Error(
          'This host does not support detail navigation or expanded display. The saved result remains available here.',
        )
      await this.app.requestDisplayMode({ mode: 'fullscreen' })
      this.onInspectView(latest)
    }
  }
  dispose(): void {
    void this.app.close()
  }
}
